/**
 * Dealers — Phase 5a implementation.
 *
 * Page composition:
 *   - PageHeader + "Add dealer" action
 *   - KPI strip (Active dealers / MTD new / Outstanding balance / Top tier)
 *   - Filter row (Tier dropdown, Branch dropdown, Active/Archived toggle)
 *   - DataTable
 *   - Detail Sheet with quick info + edit toggle + link to full page
 */

import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronDown, ExternalLink, Pencil, Plus, Users } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatRelative } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  DealerForm,
  type DealerFormValues,
} from "../../../components/forms/DealerForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  findDealerTier,
  mockDealers,
  mockDealerTiers,
  mockDealerOffers,
  mockSalesOrders,
  type MockDealer,
  type MockDealerOffer,
  type MockDealerTier,
  type MockSalesOrder,
} from "../../../lib/salesMock";
import {
  dealerOutstandingBaseCents,
  mockInvoices,
  mockPayments,
  mockCreditNotes,
  type MockInvoice,
  type MockPayment,
  type MockCreditNote,
} from "../../../lib/invoicingMock";
import { SalesOrderStatusBadge } from "../../../components/sales/SalesOrderStatusBadge";
import {
  InvoiceStatusBadge,
} from "../../../components/sales/InvoiceStatusBadge";
import { PaymentMethodBadge } from "../../../components/sales/PaymentMethodBadge";

import { mockBranches } from "../../../lib/inventoryMock";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";

export const Route = createFileRoute("/_app/sales/dealers")({
  component: DealersPage,
});

const NOW = Date.now();

type ActiveFilter = "active" | "archived";

const TIER_BADGE_VARIANT_BY_CODE: Record<
  string,
  "neutral" | "info" | "warning" | "success" | "danger"
> = {
  bronze: "neutral",
  silver: "info",
  gold: "warning",
  platinum: "success",
};

function DealersPage() {
  const isMock = useMockData();
  const navigate = useNavigate();

  const [rows, setRows] = usePersistentMockState<MockDealer[]>(
    "dealers",
    isMock ? mockDealers : [],
  );
  const [dealerTiers] = usePersistentMockState<MockDealerTier[]>(
    "dealerTiers",
    mockDealerTiers,
  );
  const [branches] = usePersistentMockState<typeof mockBranches>(
    "branches",
    mockBranches,
  );
  const [tierFilter, setTierFilter] = useState<string | "all">("all");
  const [branchFilter, setBranchFilter] = useState<string | "all">("all");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockDealer | null>(null);
  const [editingDealer, setEditingDealer] = useState<MockDealer | null>(null);

  /* ── Live invoices used to compute per-dealer outstanding A/R ── */
  const [invoicesForCredit] = usePersistentMockState<MockInvoice[]>(
    "invoices",
    mockInvoices,
  );
  const [allPayments] = usePersistentMockState<MockPayment[]>(
    "payments",
    mockPayments,
  );
  const [allCreditNotes] = usePersistentMockState<MockCreditNote[]>(
    "creditNotes",
    mockCreditNotes,
  );
  const [allSalesOrders] = usePersistentMockState<MockSalesOrder[]>(
    "salesOrders",
    mockSalesOrders,
  );
  const [allDealerOffers] = usePersistentMockState<MockDealerOffer[]>(
    "dealerOffers",
    mockDealerOffers,
  );

  /* Per-dealer live outstanding lookup. Sum across the row makes the KPI
   * total reflect actual unpaid balances rather than the stored (stale)
   * creditUsedCents on the dealer record. */
  const outstandingByDealerId = useMemo(() => {
    const m = new Map<string, bigint>();
    for (const d of rows) {
      m.set(d._id, dealerOutstandingBaseCents(d._id, invoicesForCredit));
    }
    return m;
  }, [rows, invoicesForCredit]);

  /* ── KPIs ── */
  const kpis = useMemo(() => {
    const monthStart = new Date(
      new Date(NOW).getFullYear(),
      new Date(NOW).getMonth(),
      1,
    ).getTime();
    let activeCount = 0;
    let mtdNew = 0;
    let outstanding = 0n;
    let topTierCount = 0;
    let overLimitCount = 0;
    for (const d of rows) {
      if (d.isActive) activeCount += 1;
      if (d.createdAt >= monthStart) mtdNew += 1;
      const dueBase = outstandingByDealerId.get(d._id) ?? 0n;
      outstanding += dueBase;
      if (d.creditLimitCents > 0n && dueBase > d.creditLimitCents) {
        overLimitCount += 1;
      }
      const tier = findDealerTier(d.tierId);
      if (tier && (tier.code === "platinum" || tier.code === "gold")) {
        topTierCount += 1;
      }
    }
    return { activeCount, mtdNew, outstanding, topTierCount, overLimitCount };
  }, [rows, outstandingByDealerId]);

  /* ── Filtering ── */
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (activeFilter === "active" && !r.isActive) return false;
      if (activeFilter === "archived" && r.isActive) return false;
      if (tierFilter !== "all" && r.tierId !== tierFilter) return false;
      if (branchFilter !== "all" && r.branchId !== branchFilter) return false;
      return true;
    });
  }, [rows, tierFilter, branchFilter, activeFilter]);

  /* ── Lookup maps ── */
  const tiersById = useMemo(
    () => new Map(dealerTiers.map((t) => [t._id, t])),
    [dealerTiers],
  );
  const branchesById = useMemo(
    () => new Map(branches.map((b) => [b._id, b])),
    [branches],
  );
  const tierOptions = useMemo(
    () =>
      dealerTiers.map((t) => ({ id: t._id, displayName: t.displayName })),
    [dealerTiers],
  );
  const branchOptions = useMemo(
    () => branches.map((b) => ({ id: b._id, displayName: b.displayName })),
    [branches],
  );

  /* ── Columns ── */
  const columns = useMemo<ColumnDef<MockDealer>[]>(
    () => [
      {
        accessorKey: "code",
        header: "Code",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.code}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "legal",
        header: "Legal name",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-(--text-primary)">
              {row.original.displayName}
            </span>
            {row.original.legalName ? (
              <span className="text-[11px] text-(--text-tertiary)">
                {row.original.legalName}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: "tier",
        header: "Tier",
        cell: ({ row }) => {
          const tier = tiersById.get(row.original.tierId);
          if (!tier) return <span className="text-(--text-tertiary)">—</span>;
          const variant =
            TIER_BADGE_VARIANT_BY_CODE[tier.code] ?? "neutral";
          return <Badge variant={variant}>{tier.displayName}</Badge>;
        },
      },
      {
        id: "branch",
        header: "Branch",
        cell: ({ row }) => {
          const b = branchesById.get(row.original.branchId);
          return (
            <span className="text-(--text-secondary)">
              {b?.displayName ?? row.original.branchId}
            </span>
          );
        },
        meta: { hideOnMobile: true },
      },
      {
        id: "creditLimit",
        header: "Credit limit",
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {formatMoney(row.original.creditLimitCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
        meta: { numeric: true, mono: true },
      },
      {
        id: "outstanding",
        header: "Outstanding",
        cell: ({ row }) => {
          const limit = row.original.creditLimitCents;
          const due = outstandingByDealerId.get(row.original._id) ?? 0n;
          const pct = limit > 0n ? Number((due * 1000n) / limit) / 10 : 0;
          const overLimit = limit > 0n && due > limit;
          const cls = overLimit
            ? "text-[var(--danger)] font-semibold"
            : pct >= 90
              ? "text-[var(--danger)]"
              : pct >= 60
                ? "text-[var(--warning)]"
                : "";
          return (
            <span
              className={`font-mono tabular-nums ${cls}`}
              title={
                limit > 0n
                  ? `${pct.toFixed(1)}% of limit${overLimit ? " — OVER" : ""}`
                  : "No credit limit set"
              }
            >
              {formatMoney(due, "BDT", { locale: "en-BD" })}
              {limit > 0n ? (
                <span className="ml-1.5 text-[10px] text-(--text-tertiary)">
                  {pct.toFixed(0)}%
                </span>
              ) : null}
            </span>
          );
        },
        meta: { numeric: true, mono: true },
      },
      {
        accessorKey: "paymentTermsDays",
        header: "Terms",
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {row.original.paymentTermsDays} days
          </span>
        ),
        meta: { numeric: true, hideOnMobile: true },
      },
      {
        id: "active",
        header: "Active",
        cell: ({ row }) =>
          row.original.isActive ? (
            <StatusPill variant="success" size="sm">
              Active
            </StatusPill>
          ) : (
            <StatusPill variant="neutral" size="sm">
              Archived
            </StatusPill>
          ),
      },
      {
        id: "source",
        header: "Source",
        cell: ({ row }) => (
          <span className="text-[11px] text-(--text-tertiary)">
            {row.original.importSource || "Manual"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "updatedAt",
        header: "Updated",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatRelative(row.original.updatedAt)}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
    ],
    [tiersById, branchesById, outstandingByDealerId],
  );

  /* ── Mutations ── */
  function handleCreate(values: DealerFormValues) {
    if (!isMock) return;
    const now = Date.now();
    const newDealer: MockDealer = {
      _id: `dealer_local_${now}`,
      code: values.code,
      legalName: values.legalName || null,
      displayName: values.displayName,
      tierId: values.tierId,
      branchId: values.branchId,
      primaryUserId: values.primaryUserId,
      creditLimitCents: values.creditLimitCents,
      creditLimitCurrency: values.creditLimitCurrency,
      creditUsedCents: 0n,
      paymentTermsDays: values.paymentTermsDays,
      addressJson: {
        line1: values.address.line1,
        line2: values.address.line2 || null,
        city: values.address.city,
        country: values.address.country,
      },
      contactJson: {
        phone: values.contact.phone,
        email: values.contact.email || null,
        contactPerson: values.contact.contactPerson || null,
      },
      isActive: true,
      createdAt: now,
      updatedAt: now,
      openingBalanceBaseCents: values.openingBalanceBaseCents,
      openingBalanceAsOf: values.openingBalanceAsOf,
    };
    setRows((prev) => [newDealer, ...prev]);
    setCreateOpen(false);
    toast.success(`Dealer ${newDealer.code} created (mock)`);
  }

  function handleEdit(values: DealerFormValues) {
    if (!isMock || !editingDealer) return;
    const now = Date.now();
    setRows((prev) =>
      prev.map((d) =>
        d._id === editingDealer._id
          ? {
              ...d,
              legalName: values.legalName || null,
              displayName: values.displayName,
              tierId: values.tierId,
              branchId: values.branchId,
              primaryUserId: values.primaryUserId,
              creditLimitCents: values.creditLimitCents,
              creditLimitCurrency: values.creditLimitCurrency,
              paymentTermsDays: values.paymentTermsDays,
              addressJson: {
                line1: values.address.line1,
                line2: values.address.line2 || null,
                city: values.address.city,
                country: values.address.country,
              },
              contactJson: {
                phone: values.contact.phone,
                email: values.contact.email || null,
                contactPerson: values.contact.contactPerson || null,
              },
              openingBalanceBaseCents: values.openingBalanceBaseCents,
              openingBalanceAsOf: values.openingBalanceAsOf,
              updatedAt: now,
            }
          : d,
      ),
    );
    setDetailRow((prev) =>
      prev && prev._id === editingDealer._id
        ? { ...prev, displayName: values.displayName, updatedAt: now }
        : prev,
    );
    setEditingDealer(null);
    toast.success(
      `Dealer ${editingDealer.code} updated. Audit trail recorded.`,
    );
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<MockDealer>[] = [
      { key: "code", label: "Code" },
      { key: "displayName", label: "Display name" },
      {
        key: "legalName",
        label: "Legal name",
        format: (v) => (v == null ? "" : String(v)),
      },
      {
        key: "tierId",
        label: "Tier",
        value: (r) => tiersById.get(r.tierId)?.displayName ?? "",
      },
      {
        key: "branchId",
        label: "Branch",
        value: (r) => branchesById.get(r.branchId)?.displayName ?? "",
      },
      { key: "creditLimitCents", label: "Credit limit BDT" },
      { key: "creditUsedCents", label: "Credit used BDT" },
      {
        key: "contactJson",
        label: "Contact person",
        value: (r) => r.contactJson?.contactPerson ?? "",
      },
      {
        key: "contactJson",
        label: "Phone",
        value: (r) => r.contactJson?.phone ?? "",
      },
      {
        key: "contactJson",
        label: "Email",
        value: (r) => r.contactJson?.email ?? "",
      },
      { key: "isActive", label: "Active" },
      { key: "createdAt", label: "Created", format: (v) => formatDateForCsv(Number(v)) },
    ];
    downloadCsv(
      exportFilename("dealers", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dealers"
        description="Channel partners with credit limits, tiers, and order history."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add dealer
            </Button>
          </>
        }
      />

      {/* KPI strip */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Dealer summary"
      >
        <KpiCard
          caption="Active dealers"
          value={kpis.activeCount}
          hint="Onboarded + isActive"
        />
        <KpiCard
          caption="MTD new"
          value={kpis.mtdNew}
          hint="Onboarded this month"
        />
        <KpiCard
          caption="Outstanding A/R"
          value={formatMoneySmart(kpis.outstanding, "BDT", { locale: "en-BD" })}
          hint={
            kpis.overLimitCount > 0
              ? `${kpis.overLimitCount} dealer${kpis.overLimitCount === 1 ? "" : "s"} over limit`
              : "Live across unpaid invoices"
          }
        />
        <KpiCard
          caption="Top-tier dealers"
          value={kpis.topTierCount}
          hint="Gold + Platinum"
        />
      </section>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterMenu
          label="Tier"
          options={[
            { value: "all", label: "All tiers" },
            ...dealerTiers.map((t) => ({
              value: t._id,
              label: t.displayName,
            })),
          ]}
          value={tierFilter}
          onChange={(v) => setTierFilter(v)}
        />
        <FilterMenu
          label="Branch"
          options={[
            { value: "all", label: "All branches" },
            ...branches.map((b) => ({
              value: b._id,
              label: b.displayName,
            })),
          ]}
          value={branchFilter}
          onChange={(v) => setBranchFilter(v)}
        />
        <div className="inline-flex items-center rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-0.5">
          <ToggleChip
            active={activeFilter === "active"}
            onClick={() => setActiveFilter("active")}
          >
            Active
          </ToggleChip>
          <ToggleChip
            active={activeFilter === "archived"}
            onClick={() => setActiveFilter("archived")}
          >
            Archived
          </ToggleChip>
        </div>
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No dealers match"
        emptyDescription={
          rows.length === 0
            ? "Onboard your first dealer to start receiving orders."
            : "Try widening the filters."
        }
        emptyIcon={<Users className="h-5 w-5" aria-hidden />}
        emptyAction={
          rows.length === 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add dealer
            </Button>
          ) : undefined
        }
      />

      {/* Create */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>New dealer</SheetTitle>
            <SheetDescription>
              Onboard a new channel partner. Code must be unique.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <DealerForm
              tiers={tierOptions}
              branches={branchOptions}
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRow(null);
            setEditingDealer(null);
          }
        }}
      >
        <SheetContent side="right" size="lg">
          {detailRow && editingDealer ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingDealer.code}</SheetTitle>
                <SheetDescription>
                  Update tier, credit, and contact details.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <DealerForm
                  isEdit
                  initialValues={{
                    code: editingDealer.code,
                    legalName: editingDealer.legalName ?? "",
                    displayName: editingDealer.displayName,
                    tierId: editingDealer.tierId,
                    branchId: editingDealer.branchId,
                    primaryUserId: editingDealer.primaryUserId,
                    creditLimitCents: editingDealer.creditLimitCents,
                    creditLimitCurrency: editingDealer.creditLimitCurrency,
                    paymentTermsDays: editingDealer.paymentTermsDays,
                    address: {
                      line1: editingDealer.addressJson.line1,
                      line2: editingDealer.addressJson.line2 ?? "",
                      city: editingDealer.addressJson.city,
                      country: editingDealer.addressJson.country,
                    },
                    contact: {
                      phone: editingDealer.contactJson.phone,
                      email: editingDealer.contactJson.email ?? "",
                      contactPerson:
                        editingDealer.contactJson.contactPerson ?? "",
                    },
                    openingBalanceBaseCents:
                      editingDealer.openingBalanceBaseCents ?? 0n,
                    openingBalanceAsOf:
                      editingDealer.openingBalanceAsOf ?? null,
                  }}
                  tiers={tierOptions}
                  branches={branchOptions}
                  onSubmit={handleEdit}
                  onCancel={() => setEditingDealer(null)}
                />
              </SheetBody>
            </>
          ) : detailRow ? (
            <DealerDetailPanel
              row={detailRow}
              outstandingBaseCents={
                outstandingByDealerId.get(detailRow._id) ?? 0n
              }
              invoices={invoicesForCredit}
              payments={allPayments}
              creditNotes={allCreditNotes}
              salesOrders={allSalesOrders}
              dealerOffers={allDealerOffers}
              onEdit={() => setEditingDealer(detailRow)}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail panel — read-only summary + actions.
 * ───────────────────────────────────────────────────────────── */

function DealerDetailPanel({
  row,
  outstandingBaseCents,
  invoices,
  payments,
  creditNotes,
  salesOrders,
  dealerOffers,
  onEdit,
}: {
  row: MockDealer;
  outstandingBaseCents: bigint;
  invoices: MockInvoice[];
  payments: MockPayment[];
  creditNotes: MockCreditNote[];
  salesOrders: MockSalesOrder[];
  dealerOffers: MockDealerOffer[];
  onEdit: () => void;
}) {
  const navigate = useNavigate();
  const tier = findDealerTier(row.tierId);
  const allOrders = useMemo(
    () =>
      salesOrders
        .filter((o) => o.dealerId === row._id)
        .sort((a, b) => b.placedAt - a.placedAt),
    [salesOrders, row._id],
  );
  const offerCount = useMemo(
    () => dealerOffers.filter((o) => o.dealerId === row._id).length,
    [dealerOffers, row._id],
  );
  const utilizationPct =
    row.creditLimitCents > 0n
      ? Number((outstandingBaseCents * 1000n) / row.creditLimitCents) / 10
      : 0;
  const isOverLimit =
    row.creditLimitCents > 0n && outstandingBaseCents > row.creditLimitCents;
  const availableBaseCents =
    isOverLimit
      ? 0n
      : row.creditLimitCents > 0n
        ? row.creditLimitCents - outstandingBaseCents
        : 0n;

  // Dealer-scoped data
  const dealerInvoices = useMemo(
    () =>
      invoices
        .filter((i) => i.dealerId === row._id)
        .sort((a, b) => b.issueDate - a.issueDate),
    [invoices, row._id],
  );
  const dealerPayments = useMemo(() => {
    const invIds = new Set(dealerInvoices.map((i) => i._id));
    return payments
      .filter((p) => invIds.has(p.invoiceId))
      .sort((a, b) => b.receivedAt - a.receivedAt);
  }, [payments, dealerInvoices]);
  const dealerCreditNotes = useMemo(
    () =>
      creditNotes
        .filter((c) => c.dealerId === row._id)
        .sort((a, b) => b.issueDate - a.issueDate),
    [creditNotes, row._id],
  );

  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);
  const [showAllOrders, setShowAllOrders] = useState(false);

  const visibleInvoices = showAllInvoices ? dealerInvoices : dealerInvoices.slice(0, 10);
  const visiblePayments = showAllPayments ? dealerPayments : dealerPayments.slice(0, 10);
  const visibleOrders = showAllOrders ? allOrders : allOrders.slice(0, 10);

  function formatDate(ms: number): string {
    return new Date(ms).toLocaleDateString("en-BD", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span>{row.displayName}</span>
          {tier ? (
            <Badge
              variant={
                TIER_BADGE_VARIANT_BY_CODE[tier.code] ?? "neutral"
              }
            >
              {tier.displayName}
            </Badge>
          ) : null}
          <RecordAuditBadge tableName="dealers" recordId={row._id} />
        </SheetTitle>
        <SheetDescription>
          <span className="font-mono text-[12px]">{row.code}</span>
          {row.legalName ? ` · ${row.legalName}` : null}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="creditNotes">Credit Notes</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          {/* ── Overview ── */}
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
          {/* Contact */}
          <section className="grid grid-cols-2 gap-3">
            <DetailField
              label="Contact person"
              value={row.contactJson.contactPerson ?? "—"}
            />
            <DetailField label="Phone" value={row.contactJson.phone} />
            <DetailField
              label="Email"
              value={row.contactJson.email ?? "—"}
            />
            <DetailField
              label="Address"
              value={`${row.addressJson.line1}, ${row.addressJson.city}`}
            />
          </section>

          {/* Credit */}
          <section className="rounded-[var(--radius-md)] border border-(--border-subtle) p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-[12px] text-(--text-tertiary) uppercase tracking-wide">
                Credit
              </span>
              <span
                className={
                  isOverLimit || utilizationPct >= 90
                    ? "text-[12px] font-mono tabular-nums text-[var(--danger)] font-semibold"
                    : utilizationPct >= 60
                      ? "text-[12px] font-mono tabular-nums text-[var(--warning)]"
                      : "text-[12px] font-mono tabular-nums text-(--text-secondary)"
                }
              >
                {utilizationPct.toFixed(1)}%
                {isOverLimit ? " · over" : ""}
              </span>
            </div>
            {row.creditLimitCents > 0n ? (
              <div className="h-1.5 w-full rounded-full bg-(--bg-canvas) overflow-hidden">
                <div
                  className={
                    isOverLimit || utilizationPct >= 90
                      ? "h-full bg-[var(--danger)]"
                      : utilizationPct >= 60
                        ? "h-full bg-[var(--warning)]"
                        : "h-full bg-[var(--accent)]"
                  }
                  style={{
                    width: `${Math.min(100, utilizationPct).toFixed(1)}%`,
                  }}
                />
              </div>
            ) : null}
            <div className="grid grid-cols-3 gap-3">
              <DetailField
                label="Limit"
                value={formatMoney(row.creditLimitCents, "BDT", {
                  locale: "en-BD",
                })}
              />
              <DetailField
                label="Outstanding"
                value={
                  <span
                    className={
                      isOverLimit
                        ? "text-[var(--danger)] font-semibold"
                        : utilizationPct >= 90
                          ? "text-[var(--danger)]"
                          : utilizationPct >= 60
                            ? "text-[var(--warning)]"
                            : undefined
                    }
                  >
                    {formatMoney(outstandingBaseCents, "BDT", {
                      locale: "en-BD",
                    })}
                  </span>
                }
              />
              <DetailField
                label="Available"
                value={
                  <span
                    className={
                      isOverLimit ? "text-(--text-tertiary)" : undefined
                    }
                  >
                    {formatMoney(availableBaseCents, "BDT", {
                      locale: "en-BD",
                    })}
                  </span>
                }
              />
            </div>
            <div className="text-[11px] text-(--text-tertiary)">
              Payment terms: {row.paymentTermsDays} days
              {isOverLimit ? (
                <span className="ml-2 text-[var(--danger)]">
                  · New orders blocked while over limit
                </span>
              ) : null}
            </div>
          </section>

          {/* Offers count */}
          <section className="rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2 text-[12px] flex items-center justify-between">
            <span className="text-(--text-tertiary)">Negotiated offers</span>
            <Badge variant="neutral">{offerCount}</Badge>
          </section>
            </div>
          </TabsContent>

          {/* ── Invoices ── */}
          <TabsContent value="invoices" className="mt-4">
            <div className="flex flex-col gap-3">
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                Invoices ({dealerInvoices.length})
              </div>
              {dealerInvoices.length === 0 ? (
                <div className="text-[12px] text-(--text-tertiary)">
                  No invoices for this dealer.
                </div>
              ) : (
                <>
                  <ul className="flex flex-col gap-1.5">
                    {visibleInvoices.map((inv) => (
                      <li
                        key={inv._id}
                        className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="font-mono text-[11px]">
                            {inv.invoiceNumber || "(draft)"}
                          </span>
                          <InvoiceStatusBadge status={inv.status} />
                          <span className="text-[11px] text-(--text-tertiary)">
                            {formatDate(inv.issueDate)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono tabular-nums text-[13px]">
                            {formatMoney(inv.totalAmountInBaseCents, "BDT", {
                              locale: "en-BD",
                            })}
                          </span>
                          {inv.balanceDueCents > 0n ? (
                            <span className="font-mono tabular-nums text-[11px] text-(--text-tertiary)">
                              bal {formatMoney(inv.balanceDueCents, inv.currency, {
                                locale: "en-BD",
                              })}
                            </span>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {dealerInvoices.length > 10 && !showAllInvoices ? (
                    <button
                      type="button"
                      onClick={() => setShowAllInvoices(true)}
                      className="text-[12px] text-(--accent) hover:underline self-start"
                    >
                      Show all {dealerInvoices.length} invoices →
                    </button>
                  ) : null}
                </>
              )}
              {/* Totals summary */}
              {dealerInvoices.length > 0 ? (
                <div className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-canvas) p-3 grid grid-cols-2 gap-3">
                  <DetailField
                    label="Total invoiced"
                    value={formatMoney(
                      dealerInvoices.reduce((s, i) => s + i.totalAmountInBaseCents, 0n),
                      "BDT",
                      { locale: "en-BD" },
                    )}
                  />
                  <DetailField
                    label="Total balance"
                    value={formatMoney(
                      dealerInvoices.reduce((s, i) => s + i.balanceDueCents, 0n),
                      "BDT",
                      { locale: "en-BD" },
                    )}
                  />
                </div>
              ) : null}
            </div>
          </TabsContent>

          {/* ── Payments ── */}
          <TabsContent value="payments" className="mt-4">
            <div className="flex flex-col gap-3">
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                Payment history ({dealerPayments.length})
              </div>
              {dealerPayments.length === 0 ? (
                <div className="text-[12px] text-(--text-tertiary)">
                  No payments recorded for this dealer.
                </div>
              ) : (
                <>
                  <ul className="flex flex-col gap-1.5">
                    {visiblePayments.map((p) => (
                      <li
                        key={p._id}
                        className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="font-mono text-[11px]">
                            {p.paymentNumber}
                          </span>
                          <PaymentMethodBadge method={p.paymentMethod} />
                          <span className="text-[11px] text-(--text-tertiary)">
                            {formatDate(p.receivedAt)}
                          </span>
                          {p.voidedAt ? (
                            <Badge variant="neutral">Voided</Badge>
                          ) : null}
                        </div>
                        <span
                          className={`font-mono tabular-nums text-[13px] ${
                            p.voidedAt ? "line-through text-(--text-tertiary)" : ""
                          }`}
                        >
                          {formatMoney(p.amountCents, p.currency, {
                            locale: "en-BD",
                            display: "code",
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {dealerPayments.length > 10 && !showAllPayments ? (
                    <button
                      type="button"
                      onClick={() => setShowAllPayments(true)}
                      className="text-[12px] text-(--accent) hover:underline self-start"
                    >
                      Show all {dealerPayments.length} payments →
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </TabsContent>

          {/* ── Orders ── */}
          <TabsContent value="orders" className="mt-4">
            <div className="flex flex-col gap-3">
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                Sales orders ({allOrders.length})
              </div>
              {allOrders.length === 0 ? (
                <div className="text-[12px] text-(--text-tertiary)">
                  No orders yet.
                </div>
              ) : (
                <>
                  <ul className="flex flex-col gap-1.5">
                    {visibleOrders.map((o) => (
                      <li
                        key={o._id}
                        className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-[11px]">
                            {o.orderNumber}
                          </span>
                          <SalesOrderStatusBadge status={o.status} size="sm" />
                          <span className="text-[11px] text-(--text-tertiary)">
                            {formatDate(o.placedAt)}
                          </span>
                        </div>
                        <div className="font-mono tabular-nums text-[13px]">
                          {formatMoney(o.totalInBaseCents, "BDT", {
                            locale: "en-BD",
                          })}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {allOrders.length > 10 && !showAllOrders ? (
                    <button
                      type="button"
                      onClick={() => setShowAllOrders(true)}
                      className="text-[12px] text-(--accent) hover:underline self-start"
                    >
                      Show all {allOrders.length} orders →
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </TabsContent>

          {/* ── Credit Notes ── */}
          <TabsContent value="creditNotes" className="mt-4">
            <div className="flex flex-col gap-3">
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                Credit notes ({dealerCreditNotes.length})
              </div>
              {dealerCreditNotes.length === 0 ? (
                <div className="text-[12px] text-(--text-tertiary)">
                  No credit notes for this dealer.
                </div>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {dealerCreditNotes.map((cn) => (
                    <li
                      key={cn._id}
                      className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <span className="font-mono text-[11px]">
                          {cn.creditNoteNumber}
                        </span>
                        <Badge
                          variant={cn.status === "issued" ? "success" : "neutral"}
                        >
                          {cn.status}
                        </Badge>
                        <span className="text-[11px] text-(--text-tertiary)">
                          {cn.creditReason}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono tabular-nums text-[13px]">
                          {formatMoney(cn.amountCents, cn.currency, {
                            locale: "en-BD",
                            display: "code",
                          })}
                        </span>
                        {cn.remainingAmountCents > 0n ? (
                          <span className="font-mono tabular-nums text-[11px] text-(--text-tertiary)">
                            rem {formatMoney(cn.remainingAmountCents, cn.currency, {
                              locale: "en-BD",
                            })}
                          </span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* ── History ── */}
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="dealers" recordId={row._id} />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
          <Pencil className="size-4" />
        </Button>
        <Button
          onClick={() => {
            void navigate({ to: "/sales/dealers/$dealerId", params: { dealerId: row._id } });
          }}
        >
          <ExternalLink className="h-3.5 w-3.5" /> View full page
        </Button>
      </SheetFooter>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary)">{value}</div>
    </div>
  );
}

function ToggleChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 text-[12px] rounded-md transition-colors ${active ? "bg-(--bg-selected) text-(--text-primary)" : "text-(--text-secondary) hover:text-(--text-primary)"}`}
    >
      {children}
    </button>
  );
}

function FilterMenu({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <span className="text-(--text-tertiary)">{label}:</span>
          <span className="text-(--text-primary)">
            {current?.label ?? "—"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-(--text-tertiary)" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-44">
        {options.map((o) => (
          <DropdownMenuItem key={o.value} onClick={() => onChange(o.value)}>
            {o.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
